import axios from "axios";

type AnyRec = Record<string, any>;

function firstNonEmpty(...vals: Array<string | undefined | null>) {
    return vals.find((v): v is string => typeof v === "string" && v.trim().length > 0);
}

function joinValidation(d?: AnyRec) {
    // Spring / Jakarta variants
    const arr =
        (Array.isArray(d?.errors) && d.errors) ||
        (Array.isArray(d?.violations) && d.violations) ||
        [];

    const arrMsgs = arr
        .map((x: any) => x?.message || x?.defaultMessage || x?.reason || x)
        .filter(Boolean);

    // Field-error map { field: ["msg1","msg2"] }
    const objMsgs =
        d?.errors && !Array.isArray(d.errors) && typeof d.errors === "object"
            ? Object.values(d.errors).flat().map(String)
            : [];

    const msgs = [...arrMsgs, ...objMsgs].filter(Boolean);
    return msgs.length ? msgs.join(", ") : undefined;
}

export function getErrMsg(
    e: unknown,
    fallback = "Something went wrong",
    withStatusPrefix = true
): string {
    // Plain string
    if (typeof e === "string") return e;

    // Axios error
    if (axios.isAxiosError(e)) {
        const res = e.response;
        const status = res?.status;
        const statusText =
            res?.statusText || (typeof res?.data === "object" && (res.data as AnyRec)?.error);

        let data: AnyRec | string | undefined = res?.data;

        // Try to JSON-parse string bodies
        if (typeof data === "string") {
            try {
                data = JSON.parse(data);
            } catch { /* keep as string */
            }
        }

        const d = (typeof data === "object" && data) ? (data as AnyRec) : undefined;

        const validation = joinValidation(d);
        const msg = firstNonEmpty(
            d?.message,                 // Spring "message"
            d?.detail,                  // RFC 7807
            validation,                 // collected validation messages
            typeof data === "string" ? data : undefined,
            e.message,                  // Axios' own message
            fallback
        )!;

        if (withStatusPrefix && status && statusText) {
            return `${msg}`;
        }
        return msg;
    }

    // Plain Error
    if (e instanceof Error) return e.message || fallback;

    // Unknown shape w/ message
    if (e && typeof e === "object" && "message" in (e as AnyRec)) {
        return String((e as AnyRec).message) || fallback;
    }

    return fallback;
}
